/**
 * BWF interval break model — shared by match engine, live sync, match UI, and TV display.
 */

/** @typedef {'mid_game'|'between_games'|'final_mid'} IntervalBreakKind */

/**
 * @typedef {Object} IntervalBreak
 * @property {IntervalBreakKind} kind
 * @property {number} startedAt — epoch ms
 * @property {number} gameNumber — 1-based game index for copy
 * @property {number} scoreA
 * @property {number} scoreB
 * @property {number} durationSec — 60 or 120 (informational countdown)
 * @property {boolean} showEndsChange
 */

export const INTERVAL_DURATION_MID_SEC = 60;
export const INTERVAL_DURATION_BETWEEN_SEC = 120;

/**
 * @param {IntervalBreakKind} kind
 * @param {object} params
 * @returns {IntervalBreak}
 */
export function createIntervalBreak(kind, params) {
  const {
    gameNumber,
    scoreA,
    scoreB,
    showEndsChange,
    startedAt = Date.now(),
  } = params;
  const durationSec =
    kind === "between_games"
      ? INTERVAL_DURATION_BETWEEN_SEC
      : INTERVAL_DURATION_MID_SEC;
  return {
    kind,
    startedAt,
    gameNumber,
    scoreA,
    scoreB,
    durationSec,
    showEndsChange: Boolean(showEndsChange),
  };
}

/**
 * @param {import('./matchScoring').Match} match
 * @returns {IntervalBreak|null}
 */
export function getIntervalBreak(match) {
  return match?.intervalBreak ?? null;
}

/**
 * @param {import('./matchScoring').Match} match
 * @returns {boolean}
 */
export function hasActiveIntervalBreak(match) {
  return Boolean(match?.intervalBreak);
}

/**
 * Whether the referee may optionally confirm a court/end change at this interval (BWF).
 * Tournament mode can later require confirmation via {@link isCourtChangeMandatory}.
 * @param {import('./matchScoring').Match|null|undefined} match
 * @param {IntervalBreak|null|undefined} intervalBreak
 */
export function courtChangeOfferedAtInterval(match, intervalBreak) {
  if (!intervalBreak || !match) return false;
  const { kind, gameNumber } = intervalBreak;
  return intervalOffersCourtChange(match, kind, gameNumber);
}

/** @param {IntervalBreakKind} kind @param {number} gameNumber */
export function intervalOffersCourtChange(match, kind, gameNumber) {
  if (!match) return false;
  if (kind === "final_mid") return true;
  if (kind === "mid_game") return false;
  if (kind !== "between_games") return false;
  if (gameNumber === 1) return true;
  if (gameNumber === 2 && match.bestOf === 3) {
    const winsA = match.games.filter((g) => g.gameWinner === "A").length;
    const winsB = match.games.filter((g) => g.gameWinner === "B").length;
    return winsA === 1 && winsB === 1;
  }
  return false;
}

/** Future tournament mode: when true, resume is blocked until court change is confirmed. */
export function isCourtChangeMandatory(_match, _intervalBreak) {
  return false;
}

/**
 * @param {import('./matchScoring').Match} match
 */
export function clearIntervalBreak(match) {
  if (!match?.intervalBreak) return match;
  const { intervalBreak: _removed, ...rest } = match;
  return rest;
}

/**
 * UI copy + actions for interval overlays (match tab + TV).
 * @param {IntervalBreak|null|undefined} intervalBreak
 * @returns {null|{
 *   kind: IntervalBreakKind,
 *   title: string,
 *   subtitle: string,
 *   scoreLine: string,
 *   durationSec: number,
 *   showEndsChange: boolean,
 *   primaryAction: string,
 *   gameNumber: number,
 *   startedAt: number,
 * }}
 */
export function resolveIntervalPresentation(intervalBreak) {
  if (!intervalBreak) return null;
  const { kind, gameNumber, scoreA, scoreB, durationSec, showEndsChange, startedAt } =
    intervalBreak;
  const scoreLine = `${scoreA} – ${scoreB}`;

  if (kind === "between_games") {
    return {
      kind,
      title: `Game ${gameNumber} Complete`,
      subtitle: `${durationSec} Second Interval`,
      scoreLine,
      durationSec,
      showEndsChange: Boolean(showEndsChange),
      primaryAction: "Start Next Game",
      gameNumber,
      startedAt,
      hint: showEndsChange ? null : "Change shuttle / drink break",
    };
  }

  if (kind === "final_mid") {
    return {
      kind,
      title: "Final Game Interval",
      subtitle: `${durationSec} Second Break`,
      scoreLine,
      durationSec,
      showEndsChange: Boolean(showEndsChange),
      primaryAction: "Resume Match",
      gameNumber,
      startedAt,
      hint: showEndsChange ? null : "Change shuttle / drink break",
    };
  }

  return {
    kind: "mid_game",
    title: "Interval",
    subtitle: `${durationSec} Second Break`,
    scoreLine,
    durationSec,
    showEndsChange: Boolean(showEndsChange),
    primaryAction: "Resume Match",
    gameNumber,
    startedAt,
    hint: "Change shuttle / drink break",
  };
}

/**
 * @param {number} startedAt
 * @param {number} durationSec
 * @param {number} [now]
 * @returns {number} seconds remaining (0 when elapsed)
 */
export function intervalSecondsRemaining(startedAt, durationSec, now = Date.now()) {
  const elapsed = Math.max(0, Math.floor((now - startedAt) / 1000));
  return Math.max(0, durationSec - elapsed);
}
